import { Timeline } from "../tools/timeline";

export type TimeSignature = {
  numerator: number; // beats per bar
  denominator: number; // valeur d'un beat (2 = blanche, 4 = noire, 8 = croche)
};

type Tempo = {
  bpm: number;
};

export type Bar = {
  index: number; // Numéro de la mesure (0-based)
  startTick: number; // Tick de début (inclus)
  endTick: number; // Tick de fin (exclusif)
  timeSignature: TimeSignature;
};

type TempoTable = {
  readonly ticks: number[];
  readonly bpms: number[];
  readonly secondsAt: number[];
};

/** Combien de valeurs, dans un tableau croissant, sont strictement sous `limit`. */
function countBelow(sorted: readonly number[], limit: number): number {
  let lo = 0;
  let hi = sorted.length;

  while (lo < hi) {
    const mid = (lo + hi) >> 1;

    if (sorted[mid] < limit) {
      lo = mid + 1;
    } else {
      hi = mid;
    }
  }

  return lo;
}

export class MusicalTimeline {
  private timeSignature: Timeline<TimeSignature>;
  private tempo: Timeline<Tempo>;
  private barsCache: { endTick: number; bars: Bar[] } | null = null;
  /**
   * Les changements de tempo, avec pour chacun les secondes accumulées
   * jusqu'à son tick. Invalidé par `addTempo`. C'est ce qui rend les deux
   * conversions logarithmiques : elles étaient linéaires en nombre de
   * changements, et appelées plusieurs fois par tick d'ordonnancement et par
   * image.
   */
  private tempoTableCache: TempoTable | null = null;

  constructor(
    private ppq: number = 480, // Pulses Per Quarter Note = ticks par noire
    private defaultBpm: number = 120,
    private defaulTimeSignature: TimeSignature = { numerator: 4, denominator: 4 },
  ) {
    this.timeSignature = new Timeline();
    this.tempo = new Timeline();
  }

  addTimeSignature(tick: number, numerator: number, denominator: number) {
    this.timeSignature.set(tick, { numerator, denominator });
    this.barsCache = null;
  }

  getTimeSignatureAtTick(tick: number): TimeSignature {
    return this.timeSignature.getAt(tick) ?? this.defaulTimeSignature;
  }

  getTimeSignatures(): { tick: number; ts: TimeSignature }[] {
    const tsChanges: { tick: number; ts: TimeSignature }[] = [
      { tick: 0, ts: this.defaulTimeSignature },
    ];

    for (const entry of this.timeSignature.entries()) {
      if (entry.tick === 0) {
        tsChanges[0] = { tick: 0, ts: entry.value };
      } else {
        tsChanges.push({ tick: entry.tick, ts: entry.value });
      }
    }

    return tsChanges;
  }

  addTempo(tick: number, bpm: number) {
    this.tempo.set(tick, { bpm });
    this.tempoTableCache = null;
  }

  /**
   * Les secondes cumulées sont sommées dans l'ordre des entrées, exactement
   * comme le faisait le parcours linéaire : `secondsAt[i]` est la valeur que
   * ce parcours atteignait en passant l'entrée `i`, au bit près.
   */
  private getTempoTable(): TempoTable {
    if (this.tempoTableCache) {
      return this.tempoTableCache;
    }

    const ticks: number[] = [];
    const bpms: number[] = [];
    const secondsAt: number[] = [];
    let seconds = 0;
    let prevTick = 0;
    let prevBpm = this.defaultBpm;

    for (const entry of this.tempo.entries()) {
      seconds += (entry.tick - prevTick) / this.ppq * (60 / prevBpm);
      ticks.push(entry.tick);
      bpms.push(entry.value.bpm);
      secondsAt.push(seconds);
      prevTick = entry.tick;
      prevBpm = entry.value.bpm;
    }

    this.tempoTableCache = { ticks, bpms, secondsAt };

    return this.tempoTableCache;
  }

  getPpq(): number {
    return this.ppq;
  }

  getBpmAtTick(tick: number): number {
    return this.tempo.getAt(tick)?.bpm ?? this.defaultBpm;
  }

  tickToSeconds(tick: number): number {
    const { ticks, bpms, secondsAt } = this.getTempoTable();

    // Nombre d'entrées strictement avant `tick` : celles que le parcours
    // linéaire traversait avant de s'arrêter.
    const count = countBelow(ticks, tick);

    // `0 +` et `- 0` reproduisent l'accumulation d'origine, jusqu'au signe
    // d'un zéro.
    if (count === 0) {
      return 0 + (tick - 0) / this.ppq * (60 / this.defaultBpm);
    }

    const last = count - 1;

    return secondsAt[last] + (tick - ticks[last]) / this.ppq * (60 / bpms[last]);
  }

  secondsToTick(seconds: number): number {
    const { ticks, bpms, secondsAt } = this.getTempoTable();

    // Le parcours linéaire s'arrêtait à la première entrée dont le cumul
    // atteint `seconds` ; les secondes accumulées croissent avec l'indice,
    // donc la même frontière se trouve par dichotomie.
    const count = countBelow(secondsAt, seconds);

    if (count === 0) {
      return 0 + (seconds - 0) * (this.defaultBpm / 60) * this.ppq;
    }

    const last = count - 1;
    const remainingSeconds = seconds - secondsAt[last];

    return ticks[last] + remainingSeconds * (bpms[last] / 60) * this.ppq;
  }

  /**
   * Gets the bar index at a given tick, based on the current time signature.
   */
  getBarAtTick(tick: number): number {
    const ticksPerBar = this.getTicksPerBar(tick);

    if (ticksPerBar === 0) return 0;

    return Math.floor(tick / ticksPerBar);
  }

  getBeatAtTick(tick: number): number {
    const ticksPerBeat = this.getTicksPerBeat(tick);

    if (ticksPerBeat === 0) return 0;

    const ticksPerBar = this.getTicksPerBar(tick);
    const tickInBar = tick % ticksPerBar;

    return Math.floor(tickInBar / ticksPerBeat);
  }

  getBeatsFromTicks(durationTicks: number, atTick: number): number {
    const ticksPerBeat = this.getTicksPerBeat(atTick);

    if (!ticksPerBeat) return 0;

    return durationTicks / ticksPerBeat;
  }

  getTicksPerBeat(tick: number): number {
    const ts = this.timeSignature.getAt(tick) ?? this.defaulTimeSignature;

    // denominator indique la valeur d'un beat (4 = noire = 1 quarter note)
    // ppq = ticks par quarter note, donc ticks par beat = ppq * (4 / denominator)

    return this.ppq * (4 / ts.denominator);
  }

  getTicksPerBar(tick: number): number {
    const ts = this.timeSignature.getAt(tick) ?? this.defaulTimeSignature;

    return this.getTicksPerBeat(tick) * ts.numerator;
  }

  getBars(endTick: number): Bar[] {
    if (this.barsCache && this.barsCache.endTick === endTick) {
      return this.barsCache.bars;
    }

    const bars: Bar[] = [];

    const tsChanges: { tick: number; ts: TimeSignature }[] = [
      { tick: 0, ts: this.defaulTimeSignature },
    ];

    for (const entry of Array.from(this.timeSignature.entries())) {
      if (entry.tick <= endTick) {
        if (entry.tick === 0) {
          tsChanges[0] = { tick: 0, ts: entry.value };
        } else {
          tsChanges.push({ tick: entry.tick, ts: entry.value });
        }
      }
    }

    let currentTick = 0;
    let barIndex = 0;

    for (let i = 0; i < tsChanges.length; i++) {
      const { tick: segmentStart, ts } = tsChanges[i];
      const segmentEnd = tsChanges[i + 1]?.tick ?? endTick;
      const ticksPerBar = this.ppq * (4 / ts.denominator) * ts.numerator;

      if (currentTick < segmentStart) {
        currentTick = segmentStart;
      }

      while (currentTick < segmentEnd && currentTick < endTick) {
        const barEndTick = Math.min(
          currentTick + ticksPerBar,
          segmentEnd,
          endTick,
        );

        bars.push({
          index: barIndex++,
          startTick: currentTick,
          endTick: barEndTick,
          timeSignature: ts,
        });

        currentTick = barEndTick;
      }
    }

    this.barsCache = { endTick, bars };

    return bars;
  }

  getBarCount(endTick: number): number {
    return this.getBars(endTick).length;
  }

  getBar(tick: number, endTick: number): Bar | undefined {
    const bars = this.getBars(endTick);

    let low = 0;
    let high = bars.length;

    while (low < high) {
      const mid = (low + high) >> 1;

      if (bars[mid].endTick <= tick) {
        low = mid + 1;
      } else {
        high = mid;
      }
    }

    const bar = bars[low];

    if (bar && tick >= bar.startTick && tick < bar.endTick) {
      return bar;
    }

    return undefined;
  }
}

